// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/ClinicProfile/ClinicProfilePage.tsx

import React, { useEffect, useMemo, useState } from "react";
import "./ClinicProfilePage.css";

import {
  clinicAdminApi,
  ApiError,
  ClinicAdminClinicProfileResponse,
  ClinicAdminPatchClinicProfilePayload,
  ClinicAdminClinicHoursSettings,
} from "../../../../api/clinicAdmin";

import ClinicIdentitySection from "./sections/ClinicIdentitySection";
import ClinicInformationSection from "./sections/ClinicInformationSection";
import OperatingHoursSection from "./sections/OperatingHoursSection";
import AccessibilitySection from "./sections/AccessibilitySection";
import OnSiteServicesSection from "./sections/OnSiteServicesSection";
import SpecialHoursSection from "./sections/SpecialHoursSection";
import { SkeletonCard } from "../components/Skeleton";

type IdName = { id: number; name: string };

type HoursDraftRow = {
  dayOfWeek: number; // 0..6
  isClosed: boolean;
  openTime: string; // "HH:MM" in UI
  closeTime: string; // "HH:MM" in UI
};

function formatDateTime(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function dayName(n: number) {
  const map: Record<number, string> = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };
  return map[n] || `Day ${n}`;
}

function compactTime(t: string | null | undefined) {
  if (!t) return "—";
  return String(t).slice(0, 5);
}

function toExternalUrl(raw: string | null | undefined): string | null {
  const s = String(raw || "").trim();
  if (!s) return null;

  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//.test(s)) return `https:${s}`;
  return `https://${s}`;
}

function cleanStringOrNull(s: string) {
  const v = String(s || "").trim();
  return v.length ? v : null;
}

function ensureHHMM(v: string | null | undefined): string {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s.slice(0, 5);
}

function defaultHoursDraft(): HoursDraftRow[] {
  const out: HoursDraftRow[] = [];
  for (let d = 0; d <= 6; d++) {
    out.push({
      dayOfWeek: d,
      isClosed: false,
      openTime: "09:00",
      closeTime: "17:00",
    });
  }
  return out;
}

function mergeHoursDraftFromApi(apiHours: any[]): HoursDraftRow[] {
  const base = defaultHoursDraft();
  const byDay = new Map<number, any>();

  for (const h of apiHours || []) {
    const day = Number(h?.dayOfWeek);
    if (!Number.isFinite(day)) continue;
    if (day < 0 || day > 6) continue;
    byDay.set(day, h);
  }

  return base.map((row) => {
    const h = byDay.get(row.dayOfWeek);
    if (!h) return row;

    const isClosed = !!h.isClosed;
    const openTime = ensureHHMM(h.openTime) || row.openTime;
    const closeTime = ensureHHMM(h.closeTime) || row.closeTime;

    return {
      dayOfWeek: row.dayOfWeek,
      isClosed,
      openTime,
      closeTime,
    };
  });
}

function validateHoursDraft(hours: HoursDraftRow[]): string | null {
  if (!Array.isArray(hours) || hours.length !== 7) {
    return "Hours must include exactly 7 days (Sunday–Saturday).";
  }

  const seen = new Set<number>();
  for (const r of hours) {
    const d = Number(r.dayOfWeek);
    if (!Number.isFinite(d) || d < 0 || d > 6)
      return "Invalid dayOfWeek in hours.";
    if (seen.has(d)) return "Duplicate day in hours.";
    seen.add(d);

    if (r.isClosed) continue;

    if (!r.openTime || !r.closeTime) {
      return `${dayName(d)}: open and close times are required when not closed.`;
    }

    if (r.closeTime <= r.openTime) {
      return `${dayName(d)}: close time must be after open time.`;
    }
  }

  for (let d = 0; d <= 6; d++) {
    if (!seen.has(d)) return "Hours must include all days Sunday–Saturday.";
  }

  return null;
}

function pickFirstNumber(...vals: any[]): number | null {
  for (const v of vals) {
    const n = parseInt(String(v), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

async function getLookup(url: string): Promise<IdName[]> {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  const text = await res.text().catch(() => "");
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw new Error(
      (data && (data.error || data.message)) || `Lookup failed (${res.status})`
    );
  }

  if (!Array.isArray(data)) return [];

  const rows: IdName[] = data
    .map((x: any) => {
      // Supports common lookup shapes, including:
      // - /api/clinic-settings: { ClinicSettingID, Name }
      const id = pickFirstNumber(
        x?.id,
        x?.Id,
        x?.AccessibilityFeatureID,
        x?.OnSiteServiceID,
        x?.ClinicSettingID
      );
      const name = String(x?.name ?? x?.Name ?? "").trim();
      return id && name ? { id, name } : null;
    })
    .filter(Boolean) as IdName[];

  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

function getSelectedIdsFromMultiSelect(
  e: React.ChangeEvent<HTMLSelectElement>
): number[] {
  const out: number[] = [];
  const opts = Array.from(e.target.selectedOptions || []);
  for (const o of opts) {
    const n = parseInt(String(o.value), 10);
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
}

function apiErrorMessage(e: ApiError): string {
  const b = (e as any)?.body as any;
  return (b && (b.error || b.message)) || e.message || "Request failed";
}

function defaultSpecialHoursDraft(): ClinicAdminClinicHoursSettings {
  return {
    closures: [],
    temporaryOverride: null,
    walkIn: { enabled: false, notePublic: null, hoursByDay: {} },
  };
}

/**
 * Normalize whatever comes from API into a strictly typed ClinicAdminClinicHoursSettings.
 * Key point: never allow `undefined` for reason/notePublic — use null instead.
 */
function normalizeSpecialHoursFromSettings(
  clinicSettings: any
): ClinicAdminClinicHoursSettings {
  const def = defaultSpecialHoursDraft();
  const hours =
    clinicSettings?.hours && typeof clinicSettings.hours === "object"
      ? clinicSettings.hours
      : null;
  if (!hours) return def;

  const closuresRaw = Array.isArray(hours.closures) ? hours.closures : [];
  const closures = closuresRaw.map((c: any) => ({
    date: String(c?.date || "").trim(),
    reason: c?.reason === null || typeof c?.reason === "string" ? c.reason : null,
  }));

  const tmp =
    hours.temporaryOverride && typeof hours.temporaryOverride === "object"
      ? hours.temporaryOverride
      : null;

  const temporaryOverride =
    tmp === null
      ? null
      : {
          start: String(tmp.start || "").trim(),
          end: String(tmp.end || "").trim(),
          notePublic:
            tmp.notePublic === null || typeof tmp.notePublic === "string"
              ? tmp.notePublic
              : null,
          hoursByDay:
            tmp.hoursByDay && typeof tmp.hoursByDay === "object" ? tmp.hoursByDay : {},
        };

  const w =
    hours.walkIn && typeof hours.walkIn === "object"
      ? hours.walkIn
      : { enabled: false, notePublic: null, hoursByDay: {} };

  const walkIn = {
    enabled: typeof w.enabled === "boolean" ? w.enabled : false,
    notePublic:
      w.notePublic === null || typeof w.notePublic === "string"
        ? w.notePublic
        : null,
    hoursByDay: w.hoursByDay && typeof w.hoursByDay === "object" ? w.hoursByDay : {},
  };

  return { closures, temporaryOverride, walkIn };
}

export default function ClinicProfilePage() {
  const [data, setData] = useState<ClinicAdminClinicProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<boolean>(false);

  // Save state (Clinic core info)
  const [savingClinic, setSavingClinic] = useState<boolean>(false);
  const [clinicError, setClinicError] = useState<string | null>(null);
  const [clinicSavedAt, setClinicSavedAt] = useState<string | null>(null);

  // Save state (Operating hours)
  const [savingHours, setSavingHours] = useState<boolean>(false);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [hoursSavedAt, setHoursSavedAt] = useState<string | null>(null);

  // Save state (Accessibility)
  const [savingAccessibility, setSavingAccessibility] = useState<boolean>(false);
  const [accessibilityError, setAccessibilityError] = useState<string | null>(null);
  const [accessibilitySavedAt, setAccessibilitySavedAt] =
    useState<string | null>(null);

  // Save state (On-site services)
  const [savingOnSite, setSavingOnSite] = useState<boolean>(false);
  const [onSiteError, setOnSiteError] = useState<string | null>(null);
  const [onSiteSavedAt, setOnSiteSavedAt] = useState<string | null>(null);

  // Save state (Special hours)
  const [savingSpecialHours, setSavingSpecialHours] = useState<boolean>(false);
  const [specialHoursError, setSpecialHoursError] = useState<string | null>(null);
  const [specialHoursSavedAt, setSpecialHoursSavedAt] = useState<string | null>(null);

  // Draft clinic core fields (PATCH /clinic-profile)
  // NOTE (v1): These are view-only in UI and not patched from Clinic Admin.
  // We keep ONLY setters (used to populate from API) to avoid touching other logic.
  const [, setDraftName] = useState<string>("");
  const [, setDraftAddress1] = useState<string>("");
  const [, setDraftAddress2] = useState<string>("");
  const [, setDraftPostalCode] = useState<string>("");
  const [, setDraftPhone] = useState<string>("");

  // Editable in v1
  const [draftFax, setDraftFax] = useState<string>("");
  const [draftWebsite, setDraftWebsite] = useState<string>("");
  const [draftAdminEmail, setDraftAdminEmail] = useState<string>("");

  // NOTE (v1): Lat/Long hidden and not patched from Clinic Admin. Keep setters only.
  const [, setDraftLatitude] = useState<string>("");
  const [, setDraftLongitude] = useState<string>("");

  // Clinic setting (model/type) draft + lookup
  const [draftClinicSettingId, setDraftClinicSettingId] = useState<string>("");
  const [clinicSettingOptions, setClinicSettingOptions] = useState<IdName[]>([]);

  // Draft hours
  const [draftHours, setDraftHours] = useState<HoursDraftRow[]>(defaultHoursDraft());

  // Draft accessibility feature IDs (multi-select)
  const [draftAccessibilityIds, setDraftAccessibilityIds] = useState<number[]>([]);
  const [accessibilityOptions, setAccessibilityOptions] = useState<IdName[]>([]);

  // Draft on-site service IDs (multi-select)
  const [draftOnSiteServiceIds, setDraftOnSiteServiceIds] = useState<number[]>([]);
  const [onSiteServiceOptions, setOnSiteServiceOptions] = useState<IdName[]>([]);

  // Draft special hours (ClinicSettingsJson.hours)
  const [draftSpecialHours, setDraftSpecialHours] =
    useState<ClinicAdminClinicHoursSettings>(defaultSpecialHoursDraft());

  async function loadProfile() {
    const res = await clinicAdminApi.getClinicProfile();
    setData(res);

    const c = res?.clinic;
    setDraftName(String(c?.name || ""));
    setDraftAddress1(String(c?.addressLine1 || ""));
    setDraftAddress2(String(c?.addressLine2 || ""));
    setDraftPostalCode(String(c?.postalCode || ""));
    setDraftPhone(String(c?.phone || ""));

    setDraftFax(String(c?.fax || ""));
    setDraftWebsite(String(c?.website || ""));
    setDraftAdminEmail(String(c?.administrationEmail || ""));

    setDraftLatitude(
      c?.latitude === null || c?.latitude === undefined ? "" : String(c.latitude)
    );
    setDraftLongitude(
      c?.longitude === null || c?.longitude === undefined ? "" : String(c.longitude)
    );

    // clinic setting draft (string for <select>)
    const csId =
      c?.clinicSettingId === null || c?.clinicSettingId === undefined
        ? ""
        : String(c.clinicSettingId);
    setDraftClinicSettingId(csId);

    const apiHours = Array.isArray(res?.operatingHours) ? res.operatingHours : [];
    setDraftHours(mergeHoursDraftFromApi(apiHours as any[]));

    const apiAcc = Array.isArray(res?.accessibilityFeatures)
      ? res.accessibilityFeatures
      : [];
    setDraftAccessibilityIds(
      apiAcc
        .map((x: any) => Number(x?.id))
        .filter((n: any) => Number.isFinite(n) && n > 0)
    );

    const apiOnSite = Array.isArray(res?.onSiteServices) ? res.onSiteServices : [];
    setDraftOnSiteServiceIds(
      apiOnSite
        .map((x: any) => Number(x?.id))
        .filter((n: any) => Number.isFinite(n) && n > 0)
    );

    // Special hours draft
    setDraftSpecialHours(normalizeSpecialHoursFromSettings(res?.clinicSettings));

    return { apiAcc, apiOnSite };
  }

  async function loadAccessibilityLookupWithFallback(fallback: IdName[]) {
    try {
      const opts = await getLookup("/api/accessibility-features");
      setAccessibilityOptions(opts.length ? opts : fallback);
    } catch {
      setAccessibilityOptions(fallback);
    }
  }

  async function loadOnSiteLookupWithFallback(fallback: IdName[]) {
    try {
      const opts = await getLookup("/api/on-site-services");
      setOnSiteServiceOptions(opts.length ? opts : fallback);
    } catch {
      setOnSiteServiceOptions(fallback);
    }
  }

  async function loadClinicSettingLookupWithFallback(fallback: IdName[]) {
    try {
      const opts = await getLookup("/api/clinic-settings");
      setClinicSettingOptions(opts.length ? opts : fallback);
    } catch {
      setClinicSettingOptions(fallback);
    }
  }

  function resetDraftsFromData(next: ClinicAdminClinicProfileResponse) {
    const c = next?.clinic;
    setDraftName(String(c?.name || ""));
    setDraftAddress1(String(c?.addressLine1 || ""));
    setDraftAddress2(String(c?.addressLine2 || ""));
    setDraftPostalCode(String(c?.postalCode || ""));
    setDraftPhone(String(c?.phone || ""));

    setDraftFax(String(c?.fax || ""));
    setDraftWebsite(String(c?.website || ""));
    setDraftAdminEmail(String(c?.administrationEmail || ""));

    setDraftLatitude(
      c?.latitude === null || c?.latitude === undefined ? "" : String(c.latitude)
    );
    setDraftLongitude(
      c?.longitude === null || c?.longitude === undefined ? "" : String(c.longitude)
    );

    // clinic setting draft
    const csId =
      c?.clinicSettingId === null || c?.clinicSettingId === undefined
        ? ""
        : String(c.clinicSettingId);
    setDraftClinicSettingId(csId);

    const apiHours = Array.isArray(next?.operatingHours) ? next.operatingHours : [];
    setDraftHours(mergeHoursDraftFromApi(apiHours as any[]));

    const apiAcc = Array.isArray(next?.accessibilityFeatures)
      ? next.accessibilityFeatures
      : [];
    setDraftAccessibilityIds(
      apiAcc
        .map((x: any) => Number(x?.id))
        .filter((n: any) => Number.isFinite(n) && n > 0)
    );

    const apiOnSite = Array.isArray(next?.onSiteServices) ? next.onSiteServices : [];
    setDraftOnSiteServiceIds(
      apiOnSite
        .map((x: any) => Number(x?.id))
        .filter((n: any) => Number.isFinite(n) && n > 0)
    );

    setDraftSpecialHours(normalizeSpecialHoursFromSettings(next?.clinicSettings));
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const { apiAcc, apiOnSite } = await loadProfile();
        if (!mounted) return;

        await Promise.all([
          loadAccessibilityLookupWithFallback((apiAcc as any) || []),
          loadOnSiteLookupWithFallback((apiOnSite as any) || []),
        ]);
      } catch (e: any) {
        if (!mounted) return;
        if (e instanceof ApiError) {
          setError(apiErrorMessage(e));
        } else {
          setError("Failed to load clinic profile");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure lookups load after initial profile (fallback = currently selected items)
  useEffect(() => {
    if (!data) return;

    const fallbackAcc = Array.isArray(data.accessibilityFeatures)
      ? data.accessibilityFeatures
      : [];
    const fallbackOnSite = Array.isArray(data.onSiteServices)
      ? data.onSiteServices
      : [];

    const fallbackClinicSettings: IdName[] = [];
    const csId = Number(data?.clinic?.clinicSettingId);
    const csName = String(data?.clinic?.clinicSettingName || "").trim();
    if (Number.isFinite(csId) && csId > 0 && csName) {
      fallbackClinicSettings.push({ id: csId, name: csName });
    }

    loadAccessibilityLookupWithFallback(fallbackAcc);
    loadOnSiteLookupWithFallback(fallbackOnSite);
    loadClinicSettingLookupWithFallback(fallbackClinicSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.clinic?.clinicId]);

  // Warn user when navigating away with unsaved edits
  useEffect(() => {
    if (!editMode) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [editMode]);

  const clinic = data?.clinic || null;

  const clinicWebsiteHref = useMemo(() => {
    return toExternalUrl(clinic?.website || null);
  }, [clinic?.website]);

  async function onSaveClinicCore() {
    if (!data || !clinic) return;

    setSavingClinic(true);
    setClinicError(null);
    setClinicSavedAt(null);

    try {
      // v1 rule: Clinic Admin may only edit operational contact fields + clinicSetting.
      // DO NOT patch: name/address/city/postal/phone OR latitude/longitude from this portal.
      const payload: ClinicAdminPatchClinicProfilePayload = {
        fax: cleanStringOrNull(draftFax),
        website: cleanStringOrNull(draftWebsite),
        administrationEmail: cleanStringOrNull(draftAdminEmail),
      };

      // Only send clinicSettingId if it actually changed (prevents unintended clearing).
      const currentClinicSettingId =
        clinic?.clinicSettingId === null || clinic?.clinicSettingId === undefined
          ? ""
          : String(clinic.clinicSettingId);

      if (draftClinicSettingId !== currentClinicSettingId) {
        payload.clinicSettingId = draftClinicSettingId
          ? parseInt(draftClinicSettingId, 10)
          : null;
      }

      const resp = await clinicAdminApi.patchClinicProfile(payload);

      const next: ClinicAdminClinicProfileResponse = {
        ...data,
        clinic: {
          ...data.clinic,
          ...(resp?.clinic || {}),
        },
      };

      setData(next);
      resetDraftsFromData(next);

      setClinicSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setClinicError(apiErrorMessage(e));
      } else {
        setClinicError("Failed to save clinic profile");
      }
    } finally {
      setSavingClinic(false);
    }
  }

  async function onSaveHours() {
    if (!data || !clinic) return;

    setSavingHours(true);
    setHoursError(null);
    setHoursSavedAt(null);

    try {
      const vErr = validateHoursDraft(draftHours);
      if (vErr) {
        setHoursError(vErr);
        setSavingHours(false);
        return;
      }

      const payload = {
        hours: draftHours.map((r) => ({
          dayOfWeek: r.dayOfWeek,
          isClosed: !!r.isClosed,
          openTime: r.isClosed ? null : r.openTime,
          closeTime: r.isClosed ? null : r.closeTime,
        })),
      };

      const resp = await clinicAdminApi.putClinicHours(payload);

      const next: ClinicAdminClinicProfileResponse = {
        ...data,
        operatingHours: Array.isArray(resp?.hours) ? resp.hours : data.operatingHours,
        clinic: {
          ...data.clinic,
        },
      };

      setData(next);
      resetDraftsFromData(next);

      setHoursSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setHoursError(apiErrorMessage(e));
      } else {
        setHoursError("Failed to save operating hours");
      }
    } finally {
      setSavingHours(false);
    }
  }

  async function onSaveAccessibility() {
    if (!data || !clinic) return;

    setSavingAccessibility(true);
    setAccessibilityError(null);
    setAccessibilitySavedAt(null);

    try {
      const payload = { accessibilityFeatureIds: draftAccessibilityIds };
      const resp = await clinicAdminApi.putClinicAccessibilityFeatures(payload);

      const selectedSet = new Set(
        Array.isArray(resp?.accessibilityFeatureIds) ? resp.accessibilityFeatureIds : []
      );

      const byId = new Map(accessibilityOptions.map((o) => [o.id, o]));
      const newFeatures: IdName[] = Array.from(selectedSet)
        .map((id) => {
          const opt = byId.get(id);
          return opt ? { id: opt.id, name: opt.name } : null;
        })
        .filter(Boolean) as IdName[];

      newFeatures.sort((a, b) => a.name.localeCompare(b.name));

      const next: ClinicAdminClinicProfileResponse = {
        ...data,
        accessibilityFeatures: newFeatures,
        clinic: { ...data.clinic },
      };

      setData(next);
      resetDraftsFromData(next);

      setAccessibilitySavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setAccessibilityError(apiErrorMessage(e));
      } else {
        setAccessibilityError("Failed to save accessibility features");
      }
    } finally {
      setSavingAccessibility(false);
    }
  }

  async function onSaveOnSiteServices() {
    if (!data || !clinic) return;

    setSavingOnSite(true);
    setOnSiteError(null);
    setOnSiteSavedAt(null);

    try {
      const payload = { onSiteServiceIds: draftOnSiteServiceIds };
      const resp = await clinicAdminApi.putClinicOnSiteServices(payload);

      const selectedSet = new Set(
        Array.isArray(resp?.onSiteServiceIds) ? resp.onSiteServiceIds : []
      );

      const byId = new Map(onSiteServiceOptions.map((o) => [o.id, o]));
      const newServices: IdName[] = Array.from(selectedSet)
        .map((id) => {
          const opt = byId.get(id);
          return opt ? { id: opt.id, name: opt.name } : null;
        })
        .filter(Boolean) as IdName[];

      newServices.sort((a, b) => a.name.localeCompare(b.name));

      const next: ClinicAdminClinicProfileResponse = {
        ...data,
        onSiteServices: newServices,
        clinic: { ...data.clinic },
      };

      setData(next);
      resetDraftsFromData(next);

      setOnSiteSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setOnSiteError(apiErrorMessage(e));
      } else {
        setOnSiteError("Failed to save on-site services");
      }
    } finally {
      setSavingOnSite(false);
    }
  }

  async function onSaveSpecialHours() {
    if (!data || !clinic) return;

    setSavingSpecialHours(true);
    setSpecialHoursError(null);
    setSpecialHoursSavedAt(null);

    try {
      const resp = await clinicAdminApi.patchClinicSettings({
        hours: draftSpecialHours,
      });

      const next: ClinicAdminClinicProfileResponse = {
        ...data,
        clinicSettings: resp?.clinicSettings || data.clinicSettings,
        clinic: { ...data.clinic },
      };

      setData(next);
      resetDraftsFromData(next);

      setSpecialHoursSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setSpecialHoursError(apiErrorMessage(e));
      } else {
        setSpecialHoursError("Failed to save special hours");
      }
    } finally {
      setSavingSpecialHours(false);
    }
  }

  function onCancelEdits() {
    if (!data) return;

    setEditMode(false);
    resetDraftsFromData(data);

    setClinicError(null);
    setClinicSavedAt(null);

    setHoursError(null);
    setHoursSavedAt(null);

    setAccessibilityError(null);
    setAccessibilitySavedAt(null);

    setOnSiteError(null);
    setOnSiteSavedAt(null);

    setSpecialHoursError(null);
    setSpecialHoursSavedAt(null);
  }

  if (loading) {
    return (
      <div className="ca-clinic-profile">
        <div className="ca-clinic-profile__header">
          <h1 className="ca-clinic-profile__title">Clinic Profile</h1>
        </div>
        <div className="ca-clinic-profile__panel ca-clinic-profile__skeleton">
          <SkeletonCard rows={3} />
          <SkeletonCard rows={4} />
          <SkeletonCard rows={2} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ca-clinic-profile">
        <div className="ca-clinic-profile__header">
          <h1 className="ca-clinic-profile__title">Clinic Profile</h1>
        </div>
        <div className="ca-clinic-profile__panel ca-clinic-profile__error">
          {error}
        </div>
      </div>
    );
  }

  if (!data || !clinic) return null;

  const onSiteServices: IdName[] = Array.isArray(data.onSiteServices)
    ? data.onSiteServices
    : [];
  const accessibility: IdName[] = Array.isArray(data.accessibilityFeatures)
    ? data.accessibilityFeatures
    : [];
  const hours = Array.isArray(data.operatingHours) ? data.operatingHours : [];

  return (
    <div className="ca-clinic-profile">
      <div className="ca-clinic-profile__header">
        <div className="ca-clinic-profile__header-row">
          <div>
            <h1 className="ca-clinic-profile__title">Clinic Profile</h1>
            <p className="ca-clinic-profile__subtitle">
              View and update your clinic information.
            </p>
          </div>

          {!editMode ? (
            <button
              className="ca-clinic-profile__btn"
              onClick={() => setEditMode(true)}
            >
              Edit profile
            </button>
          ) : (
            <div className="ca-clinic-profile__btn-group">
              <button
                className="ca-clinic-profile__btn ca-clinic-profile__btn--ghost"
                onClick={onCancelEdits}
              >
                Cancel
              </button>
              <button
                className="ca-clinic-profile__btn"
                onClick={() => setEditMode(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      <ClinicIdentitySection clinic={clinic} formatDateTime={formatDateTime} />

      <ClinicInformationSection
        clinic={clinic}
        editMode={editMode}
        savingClinic={savingClinic}
        draftFax={draftFax}
        setDraftFax={setDraftFax}
        draftWebsite={draftWebsite}
        setDraftWebsite={setDraftWebsite}
        draftAdminEmail={draftAdminEmail}
        setDraftAdminEmail={setDraftAdminEmail}
        clinicSettingOptions={clinicSettingOptions}
        draftClinicSettingId={draftClinicSettingId}
        setDraftClinicSettingId={setDraftClinicSettingId}
        clinicWebsiteHref={clinicWebsiteHref}
        clinicError={clinicError}
        clinicSavedAt={clinicSavedAt}
        onSaveClinicCore={onSaveClinicCore}
      />

      <OperatingHoursSection
        editMode={editMode}
        hours={hours}
        draftHours={draftHours}
        setDraftHours={setDraftHours}
        savingHours={savingHours}
        hoursError={hoursError}
        hoursSavedAt={hoursSavedAt}
        onSaveHours={onSaveHours}
        dayName={dayName}
        compactTime={compactTime}
        formatDateTime={formatDateTime}
      />

      <AccessibilitySection
        editMode={editMode}
        accessibility={accessibility}
        accessibilityOptions={accessibilityOptions}
        draftAccessibilityIds={draftAccessibilityIds}
        setDraftAccessibilityIds={setDraftAccessibilityIds}
        savingAccessibility={savingAccessibility}
        accessibilityError={accessibilityError}
        accessibilitySavedAt={accessibilitySavedAt}
        onSaveAccessibility={onSaveAccessibility}
        getSelectedIdsFromMultiSelect={getSelectedIdsFromMultiSelect}
        formatDateTime={formatDateTime}
      />

      <OnSiteServicesSection
        editMode={editMode}
        onSiteServices={onSiteServices}
        onSiteServiceOptions={onSiteServiceOptions}
        draftOnSiteServiceIds={draftOnSiteServiceIds}
        setDraftOnSiteServiceIds={setDraftOnSiteServiceIds}
        savingOnSite={savingOnSite}
        onSiteError={onSiteError}
        onSiteSavedAt={onSiteSavedAt}
        onSaveOnSiteServices={onSaveOnSiteServices}
        getSelectedIdsFromMultiSelect={getSelectedIdsFromMultiSelect}
        formatDateTime={formatDateTime}
      />

      <SpecialHoursSection
        clinicSettings={data.clinicSettings}
        editMode={editMode}
        draftSpecialHours={draftSpecialHours}
        setDraftSpecialHours={setDraftSpecialHours}
        savingSpecialHours={savingSpecialHours}
        specialHoursError={specialHoursError}
        specialHoursSavedAt={specialHoursSavedAt}
        onSaveSpecialHours={onSaveSpecialHours}
      />
    </div>
  );
}
